import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { filterInstitutions, findInstitutionByName } from '../data/undergraduateInstitutions';
import { institutionLogoCandidateUrls } from '../lib/institutionLogo';
import './AcademicInstitutionCombobox.css';

type Props = {
  id: string;
  value: string;
  onChange: (next: string) => void;
};

function InstitutionLogo({
  domain,
  className
}: {
  domain: string;
  className?: string;
}) {
  const urls = useMemo(() => institutionLogoCandidateUrls(domain), [domain]);
  const [sourceIndex, setSourceIndex] = useState(0);

  useEffect(() => {
    setSourceIndex(0);
  }, [domain]);

  if (!urls.length || sourceIndex >= urls.length) {
    return <span className={`profile-institution-fallback ${className ?? ''}`.trim()} aria-hidden />;
  }

  return (
    <img
      key={`${domain}-${sourceIndex}`}
      className={className}
      src={urls[sourceIndex]}
      alt=""
      loading="lazy"
      decoding="async"
      referrerPolicy="no-referrer"
      onError={() => setSourceIndex((i) => i + 1)}
    />
  );
}

export const AcademicInstitutionCombobox: React.FC<Props> = ({ id, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  const rootRef = useRef<HTMLDivElement>(null);
  const blurCommitTimerRef = useRef<number | null>(null);
  const draftRef = useRef(draft);

  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  const resolved = useMemo(() => findInstitutionByName(value), [value]);

  const filtered = useMemo(() => filterInstitutions(draft, 100), [draft]);

  const clearBlurTimer = () => {
    if (blurCommitTimerRef.current !== null) {
      window.clearTimeout(blurCommitTimerRef.current);
      blurCommitTimerRef.current = null;
    }
  };

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => () => clearBlurTimer(), []);

  const commit = useCallback(
    (next: string) => {
      clearBlurTimer();
      const t = next.trim();
      onChange(t);
      setDraft(t);
      setOpen(false);
    },
    [onChange]
  );

  const scheduleBlurCommit = useCallback(() => {
    clearBlurTimer();
    blurCommitTimerRef.current = window.setTimeout(() => {
      blurCommitTimerRef.current = null;
      const t = draftRef.current.trim();
      onChange(t);
      setDraft(t);
      setOpen(false);
    }, 120);
  }, [onChange]);

  return (
    <div className="profile-institution-combo" ref={rootRef}>
      <div className="profile-institution-field">
        {resolved ? (
          <InstitutionLogo domain={resolved.domain} className="profile-institution-preview" />
        ) : null}
        <input
          id={id}
          type="text"
          role="combobox"
          className="profile-institution-input"
          autoComplete="off"
          aria-autocomplete="list"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? `${id}-listbox` : undefined}
          placeholder="Search or type your undergraduate institution"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => scheduleBlurCommit()}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit(draft);
            }
          }}
        />
      </div>
      {open && (
        <ul id={`${id}-listbox`} className="profile-institution-results" role="listbox" aria-label="Institutions">
          {filtered.length === 0 ? (
            <li className="profile-institution-hint" style={{ padding: '0.5rem 0.55rem', margin: 0 }}>
              No matches. Press Enter to keep your text as the institution name.
            </li>
          ) : (
            filtered.map((inst) => (
              <li key={inst.domain + inst.name} role="presentation">
                <button
                  type="button"
                  role="option"
                  className="profile-institution-option"
                  aria-selected={value === inst.name}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    clearBlurTimer();
                    commit(inst.name);
                  }}
                >
                  <InstitutionLogo domain={inst.domain} className="profile-institution-option-logo" />
                  <span>{inst.name}</span>
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
};
